import { EnderecoFieldGroupModel, simNaoEnumToBoolean } from 'components/form'
import { isEnderecoNaoPreenchido } from 'components/form/field/endereco/endereco-utils'
import { EnderecoIndigenaFieldGroupModel } from 'components/form/field/enderecoindigena/model-enderecoIndigena'
import { ResponsavelFamiliarSelectFieldModel } from 'components/form/field/select/ResponsavelFamiliarSelectField/ResponsavelFamiliarSelectField'
import { LotacaoResponsavelCardModel } from 'components/lotacao-responsavel/LotacaoResponsavelCard'
import { LotacaoResponsavelFormModel } from 'components/lotacao-responsavel/model-lotacaoResponsavel'
import { getLotacaoResponsavel } from 'components/lotacao-responsavel/utils/utils-lotacaoResponsavel'
import { differenceInYears } from 'date-fns'
import {
  Cbo,
  CidadaoAldeadoInput,
  CidadaoAtualizacaoQuery,
  CidadaoCadsus,
  CidadaoInput,
  CidadaoVisualizacaoQuery,
  CondicoesSaudeAutorreferidasInput,
  EnderecoIndigenaInput,
  EnderecoInput,
  Escolaridade,
  InformacoesSociodemograficas,
  InformacoesSociodemograficasInput,
  InformacoesSocioEconomicas,
  InformacoesSocioEconomicasInput,
  NacionalidadeEnum,
  Pais,
  PeriodoAusencia,
  SituacaoDeRua,
  SituacaoDeRuaInput,
  TipoEnderecoEnum,
  TipoLocalNascimentoEnum,
} from 'graphql/types.generated'
import { tipoSanguineoEnum } from 'types/enums'
import { isObjectDeepEmpty, mergeDeepIgnoreNulls } from 'util/object'
import { isCnsLike } from 'util/validation/rules/cns'
import { isCpfLike } from 'util/validation/rules/cpf'
import { tipoEnderecoIndigena } from 'view/cadastro-imovel/model-cadastroImovel'
import { booleanToSimNaoEnum } from 'view/cadastro-imovel/utils-cadastroImovel'

import { isFormEmpty } from '../utils-cidadao'
import { CadsusCidadaoLocalType } from './cadsus/types/model-cadsus'
import {
  Cidadao,
  CidadaoAldeadoFormModel,
  CidadaoFormModel,
  CondicoesSaudeAutorreferidasFormModel,
  DocumentoAssociadoFormModel,
  escolaridadeRecord,
  estadoCivilRecord,
  InformacoesSociodemograficasFormModel,
  InformacoesSocioEconomicasFormModel,
  PAIS_BRASIL,
  SituacaoRuaFormModel,
} from './types/model-cidadao'

export const SEM_INFORMACAO = 'SEM INFORMAÇÃO'

type DocumentoCidadaoAtualizacaoQuery = CidadaoAtualizacaoQuery['cidadao']['documentosAssociados']

interface ConvertToCidadaoInputOptions {
  omitVinculacaoTerritorio?: boolean
  isAtendimentoSocioeducativoEnabled?: boolean
}

export function convertToCidadaoInput(
  model: CidadaoFormModel,
  { omitVinculacaoTerritorio = false, isAtendimentoSocioeducativoEnabled = true }: ConvertToCidadaoInputOptions = {}
): CidadaoInput {
  const {
    dadosPessoais,
    contatos,
    endereco,
    informacoesSocioEconomicas,
    informacoesSociodemograficas,
    condicoesSaudeAutorreferidas,
    responsabilidadeAcompanhamento,
    desativarCompartilhamentoProntuario,
  } = model

  const isNacionalidadeBrasileira = dadosPessoais.nacionalidadeCidadao.nacionalidade === NacionalidadeEnum.BRASILEIRA
  const isEmSituacaoRua = simNaoEnumToBoolean[model.statusSituacaoRua]
  const isTipoEnderecoIndigena = model.tipoEndereco !== TipoEnderecoEnum.LOGRADOURO
  const saidaTerritorio =
    dadosPessoais?.faleceu === true ? null : responsabilidadeAcompanhamento?.statusCidadaoSaiuTerritorio
  const informacoesSociodemograficasInput = convertInformacoesSociodemograficasToInput(informacoesSociodemograficas, {
    isAtendimentoSocioeducativoEnabled,
  })
  const vinculacaoCidadaoTerritorio = omitVinculacaoTerritorio
    ? undefined
    : {
        cbo2002: responsabilidadeAcompanhamento.responsavel.lotacao.cbo.cbo2002,
        cnes: responsabilidadeAcompanhamento.responsavel.lotacao.unidadeSaude.cnes,
        cns: responsabilidadeAcompanhamento.responsavel.lotacao.profissional.cns,
        ine: responsabilidadeAcompanhamento.responsavel.lotacao.equipe?.ine,
        saidaTerritorio: saidaTerritorio,
        foraDeArea: responsabilidadeAcompanhamento.statusForaArea,
        microarea: responsabilidadeAcompanhamento.microarea,
      }

  return {
    id: model.id,
    nome: dadosPessoais.nome,
    nomeSocial: dadosPessoais.nomeSocial,
    sexo: dadosPessoais.sexo,
    dataNascimento: dadosPessoais.dataNascimento,
    cpf: dadosPessoais.cpf,
    cns: dadosPessoais.cns,
    numeroDnv: dadosPessoais.numeroDnv,
    stNaoPossuiCpf: dadosPessoais.stNaoPossuiCpf,
    justificativaNaoPossuiCpf: dadosPessoais.justificativaNaoPossuiCpf,
    nisPisPasep: dadosPessoais.nisPisPasep,
    racaCor: dadosPessoais.racaCor.value,
    etniaId: dadosPessoais.etnia?.id,
    tipoSanguineoEnum: dadosPessoais.tipoSanguineoEnum?.identificador,
    estadoCivil: estadoCivilRecord[dadosPessoais.estadoCivil?.id],
    nacionalidade: dadosPessoais.nacionalidadeCidadao.nacionalidade,
    municipioNascimento: dadosPessoais.nacionalidadeCidadao.municipioNascimento?.id,
    dataEntradaPais: dadosPessoais.nacionalidadeCidadao.dataEntradaPais,
    paisNascimento: isNacionalidadeBrasileira ? PAIS_BRASIL.id : dadosPessoais.nacionalidadeCidadao.paisNascimento?.id,
    dataNaturalizacao: dadosPessoais.nacionalidadeCidadao.dataNaturalizacao,
    portariaNaturalizacao: dadosPessoais.nacionalidadeCidadao.portariaNaturalizacao,
    nomePai: dadosPessoais.nomePai,
    nomeMae: dadosPessoais.nomeMae,
    desconheceNomePai: dadosPessoais.desconheceNomePai,
    desconheceNomeMae: dadosPessoais.desconheceNomeMae,
    desejaInformarOrientacaoSexual: simNaoEnumToBoolean[dadosPessoais.desejaInformarOrientacaoSexual],
    identidadeGenero: dadosPessoais.identidadeGenero,
    desejaInformarIdentidadeGenero: simNaoEnumToBoolean[dadosPessoais.desejaInformarIdentidadeGenero],
    orientacaoSexual: dadosPessoais.orientacaoSexual,
    vinculacaoCidadaoTerritorio,
    vinculacaoCidadaoFamilia: {
      isResponsavelFamiliar: responsabilidadeAcompanhamento.isResponsavelFamiliar ?? false,
      cpfCnsResponsavelFamiliar:
        responsabilidadeAcompanhamento?.responsavelFamiliar?.cpf ||
        responsabilidadeAcompanhamento?.responsavelFamiliar?.cns,
      grauParentescoResponsavelFamiliar: responsabilidadeAcompanhamento?.grauParentescoResponsavelFamiliar,
    },
    faleceu: dadosPessoais.faleceu,
    dataObito: dadosPessoais.dataObito,
    numeroDeclaracaoObito: dadosPessoais.numeroDeclaracaoObito,
    cpfDesassociados: convertDocumentosAssociadosToInput(dadosPessoais.documentosAssociados, isCpfLike),
    cnsDesassociados: convertDocumentosAssociadosToInput(dadosPessoais.documentosAssociados, isCnsLike),
    telefoneCelular: contatos?.telefoneCelular,
    telefoneResidencial: contatos?.telefoneResidencial,
    telefoneContato: contatos?.telefoneContato,
    email: contatos?.email,
    endereco: isTipoEnderecoIndigena ? null : convertEnderecoToInput(endereco),
    municipioResidenciaExterior: endereco.municipioResidenciaExterior,
    paisResidenciaId: isTipoEnderecoIndigena ? PAIS_BRASIL.id : endereco?.pais?.id,
    informacoesSocioEconomicas: convertInformacoesSocioEconomicasToInput(informacoesSocioEconomicas),
    informacoesSociodemograficas: informacoesSociodemograficasInput,
    condicoesSaudeAutorreferidas: convertCondicoesSaudeAutorreferidasToInput(condicoesSaudeAutorreferidas),
    stCompartilhaProntuario: !desativarCompartilhamentoProntuario,
    stSituacaoDeRua: isEmSituacaoRua,
    situacaoDeRua: isEmSituacaoRua ? convertSituacaoFormModelToInput(model.situacaoRua) : null,
    isCidadaoAldeado: model.dadosPessoais.isCidadaoAldeado ?? false,
    cidadaoAldeadoInput: convertCidadaoAldeadoFormModelToInput(
      model.cidadaoAldeado,
      responsabilidadeAcompanhamento.isResponsavelFamiliar ?? false,
      model.dadosPessoais.isCidadaoAldeado
    ),
    tipoEndereco: model.tipoEndereco,
    numeroFamilia: model.enderecoIndigena?.numeroFamilia,
    tipoLocalNascimento: model.cidadaoAldeado?.tipoLocalNascimento,
    enderecoIndigenaInput:
      model?.tipoEndereco && tipoEnderecoIndigena.includes(model.tipoEndereco)
        ? convertEnderecoIndigenaFormModelToInput(model?.enderecoIndigena)
        : undefined,
  }
}

const convertDocumentosAssociadosToInput = (
  documentos: DocumentoAssociadoFormModel[],
  matchesTipo: (numeroDocumento: string) => boolean
) => documentos?.filter((doc) => doc.stRemovido && matchesTipo(doc.documento)).map((doc) => doc.documento)

export function convertToCidadaoFormModel(
  cidadao: Cidadao,
  responsavelDefaultValue?: LotacaoResponsavelFormModel
): CidadaoFormModel {
  return {
    id: cidadao.id,
    dadosPessoais: {
      nome: cidadao.nome,
      nomeSocial: cidadao?.nomeSocial,
      sexo: cidadao.sexo,
      dataNascimento: cidadao.dataNascimento,
      racaCor: {
        id: cidadao.racaCor.id,
        nome: cidadao.racaCor.nome,
        value: cidadao.racaCor.racaCorDbEnum,
      },
      etnia: cidadao?.etnia,
      tipoSanguineoEnum: {
        identificador: cidadao?.tipoSanguineoEnum,
        nome: tipoSanguineoEnum[cidadao?.tipoSanguineoEnum],
      },
      cpf: cidadao?.cpf,
      cns: cidadao?.cns,
      initialCpf: cidadao?.cpf,
      initialCns: cidadao?.cns,
      stNaoPossuiCpf: cidadao?.stNaoPossuiCpf,
      justificativaNaoPossuiCpf: cidadao?.justificativaNaoPossuiCpf,
      numeroDnv: cidadao?.numeroDnv,
      nisPisPasep: cidadao?.nisPisPasep,
      isCidadaoAldeado: !!cidadao?.cidadaoAldeado,
      nomeMae: cidadao?.nomeMae,
      nomePai: cidadao?.nomePai,
      desconheceNomeMae: cidadao?.desconheceNomeMae,
      desconheceNomePai: cidadao?.desconheceNomePai,
      desejaInformarIdentidadeGenero: booleanToSimNaoEnum(cidadao?.desejaInformarIdentidadeGenero),
      identidadeGenero: cidadao?.identidadeGeneroDbEnum,
      desejaInformarOrientacaoSexual: booleanToSimNaoEnum(cidadao?.desejaInformarOrientacaoSexual),
      orientacaoSexual: cidadao?.orientacaoSexualDbEnum,
      estadoCivil: cidadao?.estadoCivil,
      nacionalidadeCidadao: {
        nacionalidade: cidadao?.nacionalidade?.nacionalidadeDbEnum,
        municipioNascimento: {
          id: cidadao?.localidadeNascimento?.id,
          nome: cidadao?.localidadeNascimento?.nome,
          uf: cidadao?.localidadeNascimento?.uf,
          ibge: cidadao?.localidadeNascimento?.ibge,
        },
        dataEntradaPais: cidadao?.dataEntradaBrasil,
        paisNascimento: {
          id: cidadao?.paisNascimento?.id,
          nome: cidadao?.paisNascimento?.nome,
        },
        dataNaturalizacao: cidadao?.dataNaturalizacao,
        portariaNaturalizacao: cidadao?.portariaNaturalizacao,
      },
      faleceu: cidadao?.faleceu,
      dataObito: cidadao?.dataObito,
      numeroDeclaracaoObito: cidadao?.numeroDocumentoObito,
      isCpfCadsus: cidadao?.isCpfCadsus ?? false,
      stRegistroCadsus: cidadao?.stRegistroCadsus ?? false,
      documentosAssociados: convertDocumentosAssociadosToFormModel(cidadao?.documentosAssociados),
    },
    contatos: {
      telefoneResidencial: cidadao?.telefoneResidencial,
      telefoneCelular: cidadao?.telefoneCelular,
      telefoneContato: cidadao?.telefoneContato,
      email: cidadao?.email,
    },
    endereco: convertEnderecoToFormModel(cidadao?.endereco, cidadao?.localidadeExterior, cidadao?.paisExterior),
    informacoesSocioEconomicas: convertInformacoesSocioEconomicasToFormModel(cidadao?.informacoesSocioEconomicas),
    informacoesSociodemograficas: convertInformacoesSociodemograficasToFormModel(
      cidadao?.informacoesSociodemograficas,
      cidadao?.cbo,
      cidadao?.escolaridade,
      cidadao?.dataNascimento
    ),
    condicoesSaudeAutorreferidas:
      cidadao?.condicoesSaudeAutorreferidas &&
      convertCondicoesSaudeAutorreferidasToFormModel(cidadao?.condicoesSaudeAutorreferidas),
    desativarCompartilhamentoProntuario: !cidadao?.stCompartilhaProntuario,
    statusSituacaoRua: booleanToSimNaoEnum(cidadao?.situacaoDeRua?.stSituacaoDeRua),
    situacaoRua: convertToSituacaoRuaFormModel(cidadao?.situacaoDeRua),
    responsabilidadeAcompanhamento: {
      statusCidadaoSaiuTerritorio: cidadao?.cidadaoNucleoFamiliar?.mudouSe,
      microarea: cidadao?.microArea,
      statusForaArea: cidadao?.statusForaArea,
      responsavel: cidadao?.cidadaoNucleoFamiliar
        ? getLotacaoResponsavel(
            cidadao?.cidadaoNucleoFamiliar?.profissional,
            cidadao?.cidadaoNucleoFamiliar?.cbo,
            cidadao?.cidadaoNucleoFamiliar?.equipe,
            cidadao?.cidadaoNucleoFamiliar?.unidadeSaude
          )
        : responsavelDefaultValue,
      responsavelFamiliar: getResponsavelFamiliar(cidadao?.cidadaoNucleoFamiliar),
      isResponsavelFamiliar: cidadao?.cidadaoNucleoFamiliar?.isResponsavel ?? false,
      grauParentescoResponsavelFamiliar: cidadao?.cidadaoNucleoFamiliar?.grauParentescoResponsavelFamiliar,
    },
    cidadaoAldeado: convertCidadaoAldeadoToFormModel(
      cidadao?.cidadaoAldeado,
      cidadao?.tipoLocalNascimento,
      cidadao?.periodoAusenciaList
    ),
    tipoEndereco: cidadao?.tipoEndereco,
    enderecoIndigena:
      cidadao?.tipoEndereco && tipoEnderecoIndigena.includes(cidadao.tipoEndereco)
        ? convertEnderecoIndigenaToFormModel(cidadao?.enderecoIndigena, cidadao?.numeroFamilia)
        : undefined,
  }
}

const convertDocumentosAssociadosToFormModel = (
  documentos?: DocumentoCidadaoAtualizacaoQuery
): DocumentoAssociadoFormModel[] =>
  documentos?.map((doc) => ({
    documento: doc.documento,
    tipoDocumento: doc.tipoDocumento,
    nomesOutrosCidadaosMesmoDocumento: doc.outrosCidadaosMesmoDocumento.map((outro) => outro.cidadao.nome),
    permissaoExclusao: doc.permissaoExclusao,
    stRemovido: false,
  })) ?? []

export const convertInformacoesSocioEconomicasToInput = (
  form: InformacoesSocioEconomicasFormModel
): InformacoesSocioEconomicasInput => {
  const hasInformacoesSocioEconomicas =
    !!form?.alimentosAcabaramAntesTerDinheiroComprarMais || !!form?.comeuAlgunsAlimentosDinheiroAcabou

  return hasInformacoesSocioEconomicas
    ? {
        statusAlimentosAcabaramAntesTerDinheiroComprarMais:
          simNaoEnumToBoolean[form.alimentosAcabaramAntesTerDinheiroComprarMais],
        statusComeuAlgunsAlimentosQueTinhaDinheiroAcabou: simNaoEnumToBoolean[form.comeuAlgunsAlimentosDinheiroAcabou],
      }
    : null
}

export const convertInformacoesSocioEconomicasToFormModel = (
  informacoesSocioEconomicas: InformacoesSocioEconomicas
): InformacoesSocioEconomicasFormModel => {
  return {
    alimentosAcabaramAntesTerDinheiroComprarMais: informacoesSocioEconomicas
      ? booleanToSimNaoEnum(informacoesSocioEconomicas.statusAlimentosAcabaramAntesTerDinheiroComprarMais)
      : null,
    comeuAlgunsAlimentosDinheiroAcabou: informacoesSocioEconomicas
      ? booleanToSimNaoEnum(informacoesSocioEconomicas.statusComeuAlgunsAlimentosQueTinhaDinheiroAcabou)
      : null,
    openedAccordion: ['informacoesSocioEconomicas'],
  }
}

export const convertInformacoesSociodemograficasToInput = (
  form: InformacoesSociodemograficasFormModel,
  {
    isAtendimentoSocioeducativoEnabled = true,
  }: Pick<ConvertToCidadaoInputOptions, 'isAtendimentoSocioeducativoEnabled'> = {}
): InformacoesSociodemograficasInput => {
  if (isFormEmpty(form)) return null

  const input: InformacoesSociodemograficasInput = {
    escolaridade: escolaridadeRecord[form?.escolaridade?.id],
    frequentaEscolaOuCreche: simNaoEnumToBoolean[form?.frequentaEscolaOuCreche],
    criancas0a9anosComQuemFica: form?.criancas0a9anosComQuemFica,
    situacaoMercadoTrabalho: form?.situacaoMercadoTrabalho,
    ocupacaoCbo2002: form?.ocupacao?.cbo2002,
    possuiPlanoSaudePrivado: simNaoEnumToBoolean[form?.possuiPlanoSaudePrivado],
    frequentaCuidadorTradicional: simNaoEnumToBoolean[form?.frequentaCuidadorTradicional],
    participaAlgumGrupoComunitario: simNaoEnumToBoolean[form?.participaAlgumGrupoComunitario],
    isMembroComunidadeTradicional: simNaoEnumToBoolean[form?.isMembroComunidadeTradicional],
    povoComunidadeTradicional: form?.povoComunidadeTradicional,
    possuiDeficiencia: simNaoEnumToBoolean[form?.possuiDeficiencia],
    deficiencias: form?.deficiencias,
    ...(isAtendimentoSocioeducativoEnabled && {
      isAtendimentoSocioeducativo: simNaoEnumToBoolean[form?.isAtendimentoSocioeducativo],
      tipoAtendimentoSocioeducativo: form?.tipoAtendimentoSocioeducativo,
      tempoAtendimentoSocioeducativo: form?.tempoAtendimentoSocioeducativo,
    }),
  }

  return isObjectDeepEmpty(input) ? null : input
}

export const convertInformacoesSociodemograficasToFormModel = (
  informacoesSociodemograficas: InformacoesSociodemograficas,
  ocupacao: Cbo,
  escolaridade: Escolaridade,
  dataNascimento?: string
): InformacoesSociodemograficasFormModel => {
  const idade = dataNascimento ? differenceInYears(new Date(), new Date(dataNascimento)) : 0
  return (
    (informacoesSociodemograficas || ocupacao || escolaridade) && {
      escolaridade: escolaridade,
      frequentaEscolaOuCreche: booleanToSimNaoEnum(informacoesSociodemograficas?.frequentaEscolaOuCreche),
      criancas0a9anosComQuemFica: idade > 9 ? null : informacoesSociodemograficas?.criancas0a9anosComQuemFica,
      situacaoMercadoTrabalho: informacoesSociodemograficas?.situacaoMercadoTrabalho,
      ocupacao: ocupacao,
      possuiPlanoSaudePrivado: booleanToSimNaoEnum(informacoesSociodemograficas?.possuiPlanoSaudePrivado),
      frequentaCuidadorTradicional: booleanToSimNaoEnum(informacoesSociodemograficas?.frequentaCuidadorTradicional),
      participaAlgumGrupoComunitario: booleanToSimNaoEnum(informacoesSociodemograficas?.participaAlgumGrupoComunitario),
      isMembroComunidadeTradicional: booleanToSimNaoEnum(informacoesSociodemograficas?.isMembroComunidadeTradicional),
      povoComunidadeTradicional: informacoesSociodemograficas?.povoComunidadeTradicional,
      possuiDeficiencia: booleanToSimNaoEnum(informacoesSociodemograficas?.possuiDeficiencia),
      deficiencias: informacoesSociodemograficas?.deficiencias,
      isAtendimentoSocioeducativo: booleanToSimNaoEnum(informacoesSociodemograficas?.isAtendimentoSocioeducativo),
      tipoAtendimentoSocioeducativo: informacoesSociodemograficas?.tipoAtendimentoSocioeducativo,
      tempoAtendimentoSocioeducativo: informacoesSociodemograficas?.tempoAtendimentoSocioeducativo,
    }
  )
}

export const convertCondicoesSaudeAutorreferidasToInput = (
  form: CondicoesSaudeAutorreferidasFormModel
): CondicoesSaudeAutorreferidasInput => {
  return isFormEmpty(form)
    ? null
    : {
        stGestante: simNaoEnumToBoolean[form.stGestante],
        maternidadeReferencia: form.maternidadeReferencia,
        autodenominacaoPeso: form.autodenominacaoPeso,
        stAcamado: simNaoEnumToBoolean[form.stAcamado],
        stDomiciliar: simNaoEnumToBoolean[form.stDomiciliar],
        stFumante: simNaoEnumToBoolean[form.stFumante],
        stUsoAlcool: simNaoEnumToBoolean[form.stUsoAlcool],
        stUsoOutrasDrogas: simNaoEnumToBoolean[form.stUsoOutrasDrogas],
        stPraticasComplementares: simNaoEnumToBoolean[form.stPraticasComplementares],
        stProblemaSaudeMental: simNaoEnumToBoolean[form.stProblemaSaudeMental],
        stHipertensaoArterial: simNaoEnumToBoolean[form.stHipertensaoArterial],
        stDiabetes: simNaoEnumToBoolean[form.stDiabetes],
        stCancer: simNaoEnumToBoolean[form.stCancer],
        stDerrame: simNaoEnumToBoolean[form.stDerrame],
        stInfarto: simNaoEnumToBoolean[form.stInfarto],
        stHanseniase: simNaoEnumToBoolean[form.stHanseniase],
        stTuberculose: simNaoEnumToBoolean[form.stTuberculose],
        stDoencaCardiaca: simNaoEnumToBoolean[form.stDoencaCardiaca],
        doencaCardiaca: form.doencaCardiaca?.isNotEmpty() ? form.doencaCardiaca : null,
        stProblemaRins: simNaoEnumToBoolean[form.stProblemaRins],
        doencaRins: form.doencaRins?.isNotEmpty() ? form.doencaRins : null,
        stDoencaRespiratoria: simNaoEnumToBoolean[form.stDoencaRespiratoria],
        doencasRespiratorias: form.doencasRespiratorias?.isNotEmpty() ? form.doencasRespiratorias : null,
        stInternacaoUltimos12Meses: simNaoEnumToBoolean[form.stInternacaoUltimos12Meses],
        causaInternacaoUltimos12Meses: form.causaInternacaoUltimos12Meses,
        stPlantasMedicinais: simNaoEnumToBoolean[form.stPlantasMedicinais],
        plantasMedicinais: form.plantasMedicinais,
        condicaoSaude1: form.condicaoSaude1,
        condicaoSaude2: form.condicaoSaude2,
        condicaoSaude3: form.condicaoSaude3,
      }
}

export const convertCondicoesSaudeAutorreferidasToFormModel = (
  input: CondicoesSaudeAutorreferidasInput
): CondicoesSaudeAutorreferidasFormModel => ({
  openedAccordion: [],
  stGestante: booleanToSimNaoEnum(input.stGestante),
  maternidadeReferencia: input.maternidadeReferencia,
  autodenominacaoPeso: input.autodenominacaoPeso,
  stAcamado: booleanToSimNaoEnum(input.stAcamado),
  stDomiciliar: booleanToSimNaoEnum(input.stDomiciliar),
  stFumante: booleanToSimNaoEnum(input.stFumante),
  stUsoAlcool: booleanToSimNaoEnum(input.stUsoAlcool),
  stUsoOutrasDrogas: booleanToSimNaoEnum(input.stUsoOutrasDrogas),
  stPraticasComplementares: booleanToSimNaoEnum(input.stPraticasComplementares),
  stProblemaSaudeMental: booleanToSimNaoEnum(input.stProblemaSaudeMental),
  stHipertensaoArterial: booleanToSimNaoEnum(input.stHipertensaoArterial),
  stDiabetes: booleanToSimNaoEnum(input.stDiabetes),
  stCancer: booleanToSimNaoEnum(input.stCancer),
  stDerrame: booleanToSimNaoEnum(input.stDerrame),
  stInfarto: booleanToSimNaoEnum(input.stInfarto),
  stHanseniase: booleanToSimNaoEnum(input.stHanseniase),
  stTuberculose: booleanToSimNaoEnum(input.stTuberculose),
  stDoencaCardiaca: booleanToSimNaoEnum(input.stDoencaCardiaca),
  doencaCardiaca: input.doencaCardiaca,
  stProblemaRins: booleanToSimNaoEnum(input.stProblemaRins),
  doencaRins: input.doencaRins,
  stDoencaRespiratoria: booleanToSimNaoEnum(input.stDoencaRespiratoria),
  doencasRespiratorias: input.doencasRespiratorias,
  stInternacaoUltimos12Meses: booleanToSimNaoEnum(input.stInternacaoUltimos12Meses),
  causaInternacaoUltimos12Meses: input.causaInternacaoUltimos12Meses,
  stPlantasMedicinais: booleanToSimNaoEnum(input.stPlantasMedicinais),
  plantasMedicinais: input.plantasMedicinais,
  condicaoSaude1: input.condicaoSaude1,
  condicaoSaude2: input.condicaoSaude2,
  condicaoSaude3: input.condicaoSaude3,
})

export const convertEnderecoToInput = (endereco: EnderecoFieldGroupModel): EnderecoInput =>
  isEnderecoNaoPreenchido(endereco)
    ? null
    : {
        bairro: endereco?.bairro?.nome,
        cep: endereco?.cep,
        complemento: endereco?.complemento,
        logradouro: endereco?.logradouro?.nome,
        municipio: endereco?.municipio?.id,
        numero: endereco?.numero,
        pontoReferencia: endereco?.pontoReferencia,
        semNumero: endereco?.semNumero,
        tipoLogradouro: endereco?.tipoLogradouro?.id,
        uf: endereco?.uf?.id,
      }

export const convertEnderecoToFormModel = (
  endereco: Cidadao['endereco'],
  localidadeExterior: string,
  paisExterior: Pais
): EnderecoFieldGroupModel => ({
  ...endereco,
  bairro: endereco?.bairro ? { nome: endereco?.bairro } : undefined,
  logradouro: endereco?.logradouro ? { nome: endereco?.logradouro } : undefined,
  municipio: endereco?.municipio && { ...endereco.municipio, uf: { ...endereco.uf } },
  tipoLogradouro: endereco?.tipoLogradouro && { ...endereco.tipoLogradouro, numeroDne: undefined },
  uf: endereco?.uf && { ...endereco.uf },
  pais: {
    id: paisExterior?.id ?? PAIS_BRASIL.id,
    nome: paisExterior?.nome ?? PAIS_BRASIL.nome,
  },
  municipioResidenciaExterior: localidadeExterior,
})

export const convertToSituacaoRuaFormModel = (situacaoDeRua: SituacaoDeRua): SituacaoRuaFormModel => {
  return (
    situacaoDeRua && {
      grauParentescoVisitaFamiliar: situacaoDeRua.grauParentescoVisitaFamiliar,
      higienePessoal: situacaoDeRua.higienePessoal,
      possuiAcessoHigienePessoal: booleanToSimNaoEnum(situacaoDeRua.stAcessoHigienePessoal),
      instituicaoAcompanhamento: situacaoDeRua.instituicaoAcompanhamento,
      origemAlimentacao: situacaoDeRua.origemAlimentacao,
      quantidadeAlimentacaoDiaria: situacaoDeRua.quantidadeAlimentacaoDiaria,
      possuiReferenciaFamiliar: booleanToSimNaoEnum(situacaoDeRua.possuiReferenciaFamiliar),
      recebeAlgumBeneficio: booleanToSimNaoEnum(situacaoDeRua.recebeAlgumBeneficio),
      stAcompanhadoOutraInstituicao: booleanToSimNaoEnum(situacaoDeRua.stAcompanhadoOutraInstituicao),
      tempoSituacaoRua: situacaoDeRua.tempoSituacaoDeRua,
      visitaFamiliarComFrequencia: booleanToSimNaoEnum(situacaoDeRua.stVisitaFamiliarComFrequencia),
    }
  )
}

export const convertSituacaoFormModelToInput = (model: SituacaoRuaFormModel): SituacaoDeRuaInput => {
  return (
    model && {
      grauParentescoVisitaFamiliar: model.grauParentescoVisitaFamiliar,
      higienePessoal: model.higienePessoal,
      instituicaoAcompanhamento: model.instituicaoAcompanhamento,
      origemAlimentacao: model.origemAlimentacao,
      quantidadeAlimentacaoDiaria: model.quantidadeAlimentacaoDiaria,
      tempoSituacaoDeRua: model.tempoSituacaoRua,
      possuiReferenciaFamiliar: simNaoEnumToBoolean[model.possuiReferenciaFamiliar],
      recebeAlgumBeneficio: simNaoEnumToBoolean[model.recebeAlgumBeneficio],
      stAcessoHigienePessoal: simNaoEnumToBoolean[model.possuiAcessoHigienePessoal],
      stAcompanhadoOutraInstituicao: simNaoEnumToBoolean[model.stAcompanhadoOutraInstituicao],
      stVisitaFamiliarComFrequencia: simNaoEnumToBoolean[model.visitaFamiliarComFrequencia],
    }
  )
}

export const convertCidadaoAldeadoToFormModel = (
  cidadaoAldeado: Cidadao['cidadaoAldeado'],
  tipoLocalNascimento: TipoLocalNascimentoEnum,
  periodoAusenciaList?: ReadonlyArray<PeriodoAusencia>
): CidadaoAldeadoFormModel => {
  return {
    aldeiaNascimento: cidadaoAldeado?.aldeiaNascimento,
    funcaoSocial: cidadaoAldeado?.funcaoSocial,
    localOcorrencia: cidadaoAldeado?.localOcorrencia,
    nomeTradicional: cidadaoAldeado?.nomeTradicional,
    responsavelLegal: cidadaoAldeado?.responsavelLegal,
    dtReconhecimento: cidadaoAldeado?.dtReconhecimento,
    stChefeFamilia: booleanToSimNaoEnum(cidadaoAldeado?.stChefeFamilia),
    periodoAusenciaList:
      periodoAusenciaList?.map((item) => ({
        id: item.id,
        dtAusencia: item.dtAusencia,
        dtRetorno: item.dtRetorno,
      })) ?? [],
    unidadeFunai: cidadaoAldeado?.unidadeFunai,
    beneficios: cidadaoAldeado?.beneficios ?? [],
    livro: cidadaoAldeado?.livro,
    folha: cidadaoAldeado?.folha,
    cadastroUnico: cidadaoAldeado?.cadastroUnico,
    dtEmissao: cidadaoAldeado?.dtEmissao,
    ufNascimento: cidadaoAldeado?.ufNascimento,
    tipoLocalNascimento: tipoLocalNascimento,
  }
}

export const convertCidadaoAldeadoFormModelToInput = (
  cidadaoAldeado: CidadaoAldeadoFormModel,
  isChefeFamilia: boolean,
  isCidadaoAldeado: boolean
): CidadaoAldeadoInput => {
  return isCidadaoAldeado
    ? {
        aldeiaNascimentoId: cidadaoAldeado?.aldeiaNascimento?.id,
        funcaoSocial: cidadaoAldeado?.funcaoSocial?.id,
        localOcorrenciaId: cidadaoAldeado?.localOcorrencia?.id,
        nomeTradicional: cidadaoAldeado?.nomeTradicional,
        responsavelLegal: cidadaoAldeado?.responsavelLegal,
        stChefeFamilia: isChefeFamilia,
        dtReconhecimento: cidadaoAldeado?.dtReconhecimento,
        beneficios: cidadaoAldeado?.beneficios ?? [],
        dtAusencia: cidadaoAldeado?.dtAusencia,
        dtRetorno: cidadaoAldeado?.dtRetorno,
        unidadeFunai: cidadaoAldeado?.unidadeFunai,
        livro: cidadaoAldeado?.livro,
        folha: cidadaoAldeado?.folha,
        cadastroUnico: cidadaoAldeado?.cadastroUnico,
        dtEmissao: cidadaoAldeado?.dtEmissao,
        ufNascimentoId: cidadaoAldeado?.ufNascimento?.id,
      }
    : null
}

export const convertToLotacaoResponsavelCardModel = (
  nucleoFamiliar: CidadaoVisualizacaoQuery['cidadao']['cidadaoNucleoFamiliar']
): LotacaoResponsavelCardModel => {
  const { profissional, cbo, unidadeSaude, equipe } = nucleoFamiliar || {}

  return {
    profissional: profissional
      ? {
          id: profissional.id ?? '',
          nome: profissional.nome ?? '',
          nomeSocial: profissional.nomeSocial ?? undefined,
        }
      : null,

    cbo: cbo ? { id: cbo.id ?? '', nome: cbo.nome ?? '' } : null,

    unidadeSaude: unidadeSaude
      ? { id: unidadeSaude.id ?? '', nome: unidadeSaude.nome ?? '', cnes: unidadeSaude.cnes ?? '' }
      : null,

    equipe: equipe ? { id: equipe.id ?? '', nome: equipe.nome ?? '', ine: equipe.ine ?? '' } : undefined,
  }
}

export function getResponsavelFamiliar(
  nucleoFamiliar: Cidadao['cidadaoNucleoFamiliar']
): ResponsavelFamiliarSelectFieldModel | null {
  if (!nucleoFamiliar) {
    return null
  }

  if (nucleoFamiliar.responsavelFamiliar) {
    const responsavelFamiliar = nucleoFamiliar.responsavelFamiliar
    return {
      id: responsavelFamiliar.id,
      nome: responsavelFamiliar.nome,
      nomeSocial: responsavelFamiliar.nomeSocial,
      cpf: responsavelFamiliar.cpf,
      cns: responsavelFamiliar.cns,
      statusResponsavelFamiliar: responsavelFamiliar.statusResponsavelFamiliar,
      uuidUltimaFicha: responsavelFamiliar.uuidUltimaFicha,
    }
  }

  if (nucleoFamiliar.cpfCnsResponsavel) {
    const cpfCns = nucleoFamiliar.cpfCnsResponsavel
    return {
      cpf: (isCpfLike(cpfCns) && cpfCns) || null,
      cns: (isCnsLike(cpfCns) && cpfCns) || null,
    }
  }

  return null
}

export const convertEnderecoIndigenaToFormModel = (
  enderecoIndigena: Cidadao['enderecoIndigena'],
  numeroFamilia: string
): EnderecoIndigenaFieldGroupModel => {
  return {
    aldeia: enderecoIndigena?.aldeiaResidencia && {
      ...enderecoIndigena.aldeiaResidencia,
      municipio: [{ ...enderecoIndigena?.municipio, uf: enderecoIndigena?.uf }],
      poloBase: enderecoIndigena?.poloBaseResidencia
        ? {
            ...enderecoIndigena.poloBaseResidencia,
            dsei: enderecoIndigena?.dseiResidencia,
          }
        : undefined,
    },
    dsei: enderecoIndigena?.dseiResidencia,
    poloBase: enderecoIndigena?.poloBaseResidencia
      ? {
          ...enderecoIndigena.poloBaseResidencia,
          dsei: enderecoIndigena?.dseiResidencia,
        }
      : undefined,
    municipio: enderecoIndigena?.municipio && {
      ...enderecoIndigena.municipio,
      uf: enderecoIndigena?.uf,
    },
    uf: enderecoIndigena?.uf,
    numero: enderecoIndigena?.numero,
    numeroFamilia: numeroFamilia,
  }
}

export const convertEnderecoIndigenaFormModelToInput = (
  enderecoIndigena: EnderecoIndigenaFieldGroupModel
): EnderecoIndigenaInput => {
  return isObjectDeepEmpty(enderecoIndigena)
    ? null
    : {
        aldeiaResidenciaId: enderecoIndigena?.aldeia?.id,
        dseiResidenciaId: enderecoIndigena?.dsei?.id,
        poloBaseResidenciaId: enderecoIndigena?.poloBase?.id,
        municipioId: enderecoIndigena?.municipio?.id,
        ufId: enderecoIndigena?.uf?.id,
        numero: enderecoIndigena?.numero,
      }
}

export const convertCadsusCidadaoLocalTypeToFormModel = (
  cidadaoLocal: CadsusCidadaoLocalType,
  cpfCadsus?: string,
  cnsCadsus?: string
): CidadaoFormModel => {
  return {
    id: cidadaoLocal?.id,
    dadosPessoais: {
      cpf: cidadaoLocal?.cpf,
      cns: cidadaoLocal?.cns,
      nisPisPasep: cidadaoLocal?.nisPisPasep,
      nome: cidadaoLocal?.nome,
      nomeSocial: cidadaoLocal?.nomeSocial,
      dataNascimento: cidadaoLocal?.dataNascimento,
      sexo: cidadaoLocal?.sexo,
      estadoCivil: cidadaoLocal?.estadoCivil
        ? { id: cidadaoLocal?.estadoCivil.id, nome: cidadaoLocal?.estadoCivil.nome }
        : null,
      tipoSanguineoEnum: cidadaoLocal?.tipoSanguineoEnum
        ? { identificador: cidadaoLocal.tipoSanguineoEnum, nome: tipoSanguineoEnum[cidadaoLocal.tipoSanguineoEnum] }
        : null,
      racaCor: cidadaoLocal?.racaCor
        ? {
            id: cidadaoLocal.racaCor.id,
            nome: cidadaoLocal.racaCor.nome,
            value: cidadaoLocal.racaCor.racaCorDbEnum,
          }
        : null,
      etnia: cidadaoLocal?.etnia ? { id: cidadaoLocal.etnia.id, nome: cidadaoLocal.etnia.nome } : null,
      nomeMae: cidadaoLocal?.nomeMae,
      nomePai: cidadaoLocal?.nomePai,
      desconheceNomeMae: cidadaoLocal?.desconheceNomeMae ?? false,
      desconheceNomePai: cidadaoLocal?.desconheceNomePai ?? false,
      nacionalidadeCidadao: {
        nacionalidade: cidadaoLocal?.nacionalidade?.nacionalidadeDbEnum,
        municipioNascimento: cidadaoLocal?.localidadeNascimento
          ? {
              id: cidadaoLocal.localidadeNascimento.id,
              nome: cidadaoLocal.localidadeNascimento.nome,
              uf: cidadaoLocal.localidadeNascimento.uf,
            }
          : null,
        portariaNaturalizacao: cidadaoLocal?.portariaNaturalizacao,
        dataNaturalizacao: cidadaoLocal?.dataNaturalizacao,
        paisNascimento: cidadaoLocal?.paisNascimento
          ? { id: cidadaoLocal.paisNascimento.id, nome: cidadaoLocal.paisNascimento.nome }
          : null,
        dataEntradaPais: cidadaoLocal?.dataEntradaBrasil,
      },
      desejaInformarOrientacaoSexual: booleanToSimNaoEnum(cidadaoLocal?.desejaInformarOrientacaoSexual),
      orientacaoSexual: cidadaoLocal?.orientacaoSexualDbEnum,
      desejaInformarIdentidadeGenero: booleanToSimNaoEnum(cidadaoLocal?.desejaInformarIdentidadeGenero),
      identidadeGenero: cidadaoLocal?.identidadeGeneroDbEnum,
      faleceu: cidadaoLocal?.faleceu,
      dataObito: cidadaoLocal?.dataObito,
      numeroDeclaracaoObito: cidadaoLocal?.numeroDocumentoObito,
      isCpfCadsus: !!cpfCadsus || cidadaoLocal?.isCpfCadsus,
      stRegistroCadsus: !!cnsCadsus || cidadaoLocal?.stRegistroCadsus,
      documentosAssociados: [],
    },
    contatos: {
      telefoneResidencial: cidadaoLocal?.telefoneResidencial,
      telefoneCelular: cidadaoLocal?.telefoneCelular,
      telefoneContato: cidadaoLocal?.telefoneContato,
      email: cidadaoLocal?.email,
    },
    endereco: cidadaoLocal?.endereco
      ? {
          bairro: cidadaoLocal.endereco.bairro ? { nome: cidadaoLocal.endereco.bairro } : null,
          cep: cidadaoLocal.endereco.cep,
          complemento: cidadaoLocal.endereco.complemento,
          logradouro: cidadaoLocal.endereco.logradouro ? { nome: cidadaoLocal.endereco.logradouro } : null,
          numero: cidadaoLocal.endereco.numero,
          pontoReferencia: cidadaoLocal.endereco.pontoReferencia,
          semNumero: cidadaoLocal.endereco.semNumero ?? false,
          municipio: cidadaoLocal.endereco.municipio
            ? {
                id: cidadaoLocal.endereco.municipio.id,
                nome: cidadaoLocal.endereco.municipio.nome,
                uf: cidadaoLocal.endereco.municipio.uf,
              }
            : null,
          tipoLogradouro: cidadaoLocal.endereco.tipoLogradouro
            ? {
                id: cidadaoLocal.endereco.tipoLogradouro.id,
                nome: cidadaoLocal.endereco.tipoLogradouro.nome,
                numeroDne: cidadaoLocal.endereco.tipoLogradouro.numeroDne,
              }
            : null,
          uf: cidadaoLocal.endereco.uf,
          pais: cidadaoLocal.paisExterior ?? PAIS_BRASIL,
          municipioResidenciaExterior: cidadaoLocal.localidadeExterior,
        }
      : null,
    desativarCompartilhamentoProntuario: !cidadaoLocal?.stCompartilhaProntuario,
    tipoEndereco: cidadaoLocal?.tipoEndereco,
    enderecoIndigena: { ...cidadaoLocal?.enderecoIndigena, numeroFamilia: cidadaoLocal?.numeroFamilia },
    responsabilidadeAcompanhamento: null,
    informacoesSocioEconomicas: null,
    informacoesSociodemograficas: null,
    statusSituacaoRua: null,
    situacaoRua: null,
    condicoesSaudeAutorreferidas: null,
    cidadaoAldeado: null,
  }
}

export const convertCadsusToFormModel = (
  cidadaoLocal: CidadaoCadsus,
  responsavel: LotacaoResponsavelFormModel
): CidadaoFormModel => {
  return {
    dadosPessoais: {
      cpf: cidadaoLocal?.cpf,
      cns: cidadaoLocal?.cns,
      nome: cidadaoLocal?.nome,
      nomeSocial: cidadaoLocal?.nomeSocial,
      nomeMae: cidadaoLocal?.nomeMae !== SEM_INFORMACAO ? cidadaoLocal?.nomeMae : null,
      nomePai: cidadaoLocal?.nomePai !== SEM_INFORMACAO ? cidadaoLocal?.nomePai : null,
      racaCor: {
        id: cidadaoLocal?.racaCor.id,
        nome: cidadaoLocal?.racaCor.nome,
        value: cidadaoLocal?.racaCor.racaCorDbEnum,
      },
      etnia: cidadaoLocal?.etnia ? { id: cidadaoLocal?.etnia.id, nome: cidadaoLocal?.etnia.nome } : null,
      nacionalidadeCidadao: {
        nacionalidade: cidadaoLocal?.nacionalidade?.nacionalidadeDbEnum,
        municipioNascimento: cidadaoLocal?.municipioNascimento,
        paisNascimento: cidadaoLocal?.paisNascimento
          ? { id: cidadaoLocal?.paisNascimento.id, nome: cidadaoLocal?.paisNascimento.nome }
          : null,
        portariaNaturalizacao: cidadaoLocal?.portariaNaturalizacao,
        dataEntradaPais: cidadaoLocal?.dataEntradaBrasil,
        dataNaturalizacao: cidadaoLocal?.dataNaturalizacao,
      },
      dataNascimento: cidadaoLocal?.dataNascimento,
      dataObito: cidadaoLocal?.dataObito,
      sexo: cidadaoLocal?.sexo,
      faleceu: cidadaoLocal?.dataObito,
      nisPisPasep: cidadaoLocal?.numeroPisPasep,
      desconheceNomeMae: !cidadaoLocal?.nomeMae || cidadaoLocal?.nomeMae === SEM_INFORMACAO,
      desconheceNomePai: !cidadaoLocal?.nomePai || cidadaoLocal?.nomePai === SEM_INFORMACAO,
      identidadeGenero: null,
      desejaInformarIdentidadeGenero: null,
      orientacaoSexual: null,
      desejaInformarOrientacaoSexual: null,
      isCpfCadsus: !!cidadaoLocal?.cpf,
      stRegistroCadsus: !!cidadaoLocal?.cns,
      documentosAssociados: [],
    },
    contatos: {
      telefoneResidencial: cidadaoLocal?.telefoneResidencial[0],
      telefoneCelular: cidadaoLocal?.telefoneCelular[0],
      telefoneContato: cidadaoLocal?.telefoneContato[0],
      email: cidadaoLocal?.email,
    },
    endereco: {
      bairro: cidadaoLocal?.endereco?.bairro ? { nome: cidadaoLocal?.endereco?.bairro } : null,
      cep: cidadaoLocal?.endereco?.cep,
      complemento: cidadaoLocal?.endereco?.complemento,
      logradouro: cidadaoLocal?.endereco?.logradouro ? { nome: cidadaoLocal?.endereco?.logradouro } : null,
      numero: cidadaoLocal?.endereco?.numero,
      pontoReferencia: cidadaoLocal?.endereco?.pontoReferencia,
      semNumero: cidadaoLocal?.endereco?.semNumero ?? false,
      municipio: cidadaoLocal?.endereco?.municipio
        ? {
            id: cidadaoLocal?.endereco.municipio.id,
            nome: cidadaoLocal?.endereco.municipio.nome,
            uf: cidadaoLocal?.endereco.municipio.uf,
          }
        : null,
      tipoLogradouro: cidadaoLocal?.endereco?.tipoLogradouro
        ? {
            id: cidadaoLocal?.endereco.tipoLogradouro.id,
            nome: cidadaoLocal?.endereco.tipoLogradouro.nome,
            numeroDne: cidadaoLocal?.endereco.tipoLogradouro.numeroDne,
          }
        : null,
      uf: cidadaoLocal?.endereco?.uf,
      municipioResidenciaExterior: cidadaoLocal?.municipioResidenciaExterior,
      pais: cidadaoLocal?.paisResidencia ?? PAIS_BRASIL,
    },
    responsabilidadeAcompanhamento: {
      responsavel: responsavel,
      grauParentescoResponsavelFamiliar: null,
      isResponsavelFamiliar: null,
      responsavelFamiliar: null,
    },
    tipoEndereco: TipoEnderecoEnum.LOGRADOURO,
    informacoesSocioEconomicas: null,
    informacoesSociodemograficas: null,
    condicoesSaudeAutorreferidas: null,
    desativarCompartilhamentoProntuario: null,
    statusSituacaoRua: null,
    situacaoRua: null,
    cidadaoAldeado: null,
  }
}

function sanitizeCidadaoFormModel(model: CidadaoFormModel): CidadaoFormModel {
  if (model.dadosPessoais?.cpf) {
    model.dadosPessoais.stNaoPossuiCpf = false
    model.dadosPessoais.justificativaNaoPossuiCpf = null
  }

  return model
}

export function mergeCadsusToFormModel(
  localModel: CidadaoFormModel,
  cadsusModel: CidadaoFormModel | null
): CidadaoFormModel {
  if (!cadsusModel) {
    return localModel
  }

  const mergedModel = mergeDeepIgnoreNulls(localModel, cadsusModel)

  return sanitizeCidadaoFormModel(mergedModel)
}
